import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Mark } from "@/components/Mark";
import { NAV, SITE } from "@/lib/site";

export const Footer = () => {
  const year = new Date().getFullYear();
  const wordmarkRef = useRef<HTMLSpanElement>(null);
  const [wordmarkScale, setWordmarkScale] = useState(1);

  useEffect(() => {
    const el = wordmarkRef.current;
    if (!el) return;
    const parent = el.parentElement as HTMLElement;
    const fit = () => {
      const ratio = parent.clientWidth / el.scrollWidth;
      setWordmarkScale(ratio > 0 && ratio < 5 ? ratio : 1);
    };
    fit();
    document.fonts.ready.then(fit).catch(() => {});
    document.fonts.addEventListener("loadingdone", fit);
    window.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      document.fonts.removeEventListener("loadingdone", fit);
    };
  }, []);

  return (
    <footer className="border-t border-line bg-bg" data-testid="site-footer">
      <div className="mx-auto max-w-[1600px] px-5 pb-8 pt-16 sm:px-10 sm:pt-24 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <Mark className="h-5 w-5 text-ink" />
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-ink">
                Sonoco Events<sup className="ml-0.5 text-[9px] text-mute">®</sup>
              </span>
            </div>
            <p className="mt-6 max-w-sm text-2xl font-light leading-snug text-mute">
              La technique au service de vos idées.
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
              Production technique événementielle — B2B
            </p>
          </div>

          <nav className="lg:col-span-3" aria-label="Navigation pied de page">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">Navigation</p>
            <ul className="mt-6 space-y-3">
              {NAV.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`footer-nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="link-sweep text-sm uppercase tracking-[0.12em] text-mute transition-colors duration-300 hover:text-ink"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">Contact</p>
            <ul className="mt-6 space-y-3 text-sm text-mute">
              <li>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email-link"
                  className="link-sweep transition-colors duration-300 hover:text-ink"
                >
                  {SITE.email}
                </a>
              </li>
              {SITE.phones.map((p) => (
                <li key={p}>
                  <a
                    href={`tel:${p.replace(/\s/g, "")}`}
                    data-testid="footer-phone-link"
                    className="link-sweep transition-colors duration-300 hover:text-ink"
                  >
                    {p}
                  </a>
                </li>
              ))}
              <li className="pt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                {SITE.location}
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">Suivre</p>
            <ul className="mt-6 space-y-3 text-sm text-mute">
              <li>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="link-sweep inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-ink"
                >
                  Instagram <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
              <li className="pt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-faint">
                Sud de la France
                <br />
                & PACA
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 select-none overflow-hidden" aria-hidden="true">
          <span
            ref={wordmarkRef}
            data-testid="footer-wordmark"
            style={{ transform: `scale(${wordmarkScale})`, transformOrigin: "left bottom" }}
            className="block whitespace-nowrap text-[13.5vw] font-extrabold uppercase leading-[0.8] tracking-tightest text-surface"
          >
            Sonoco<span className="text-panel">Events</span>
          </span>
        </div>

        <div className="mt-8 flex flex-col justify-between gap-3 border-t border-line pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-faint sm:flex-row sm:items-center">
          <span data-testid="footer-copyright">
            © {year} Sonoco Events — Tous droits réservés
          </span>
          <span>{SITE.location}</span>
          <Link
            to="/mentions-legales"
            data-testid="footer-mentions-legales"
            className="transition-colors duration-300 hover:text-ink"
          >
            Mentions légales · RGPD
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
